<template>
  <div class="stats-page">
    <div class="card">
      <div class="head">
        <el-button icon="el-icon-arrow-left" @click="goBack">{{ $t('actions.back') }}</el-button>
        <div>
          <h2 class="title">{{ isEn ? 'Statistics' : '统计结果' }}</h2>
          <p class="desc">
            {{ isEn
              ? 'Select All Data to show the patient list and batch export selected CSV files; select one patient to view its statistics table.'
              : '选择全部数据时显示患者列表，可多选后批量下载；选择单个患者时显示该患者统计表。' }}
          </p>
        </div>
        <div class="spacer"></div>
        <el-select v-model="statsScope" size="small" class="stats-select" @change="onScopeChange">
          <el-option :label="isEn ? 'All Data' : '全部数据'" value="__all__" />
          <el-option
            v-for="row in rows"
            :key="row.display"
            :label="`${row.patient} · ${row.date}`"
            :value="row.display"
          />
        </el-select>
        <el-button
          v-if="isAllScope"
          size="small"
          plain
          :disabled="!rows.length"
          @click="toggleSelectAllPatients"
        >
          {{ allPatientsSelected ? (isEn ? 'Deselect All' : '全不选') : (isEn ? 'Select All' : '全选') }}
        </el-button>
        <el-button
          v-if="isAllScope"
          size="small"
          type="success"
          plain
          :disabled="!selectedRows.length"
          @click="openBatchMiddleStats"
        >
          {{ isEn ? 'Analyze Selected' : '统计选中数据' }}
        </el-button>
        <el-button size="small" type="primary" plain :loading="statsLoading || loading" @click="refreshCurrent">
          {{ isEn ? 'Refresh Statistics' : '刷新统计' }}
        </el-button>
        <el-button size="small" type="primary" :disabled="downloadDisabled" :loading="downloading" @click="downloadStatsCsv">
          {{ isEn ? 'Download CSV' : '下载CSV' }}
        </el-button>
      </div>

      <template v-if="isAllScope">
        <el-table
          :data="rows"
          border
          size="small"
          v-loading="loading"
          :empty-text="isEn ? 'No patient data' : '暂无患者数据'"
          style="width: 100%"
        >
          <el-table-column width="60" align="center">
            <template slot-scope="{ row }">
              <el-checkbox
                :value="selectedKeys.includes(row.display)"
                @change="checked => togglePatient(row, checked)"
              />
            </template>
          </el-table-column>
          <el-table-column prop="patient" :label="$t('fields.patient')" min-width="240" />
          <el-table-column prop="date" :label="$t('fields.date')" width="180" />
        </el-table>

      </template>

      <el-table
        v-else
        :data="statsRows"
        border
        size="small"
        v-loading="statsLoading || loading"
        :empty-text="isEn ? 'No statistics data' : '暂无统计数据'"
        style="width: 100%"
      >
        <el-table-column prop="label" :label="isEn ? 'Metric' : '指标'" min-width="240" />
        <el-table-column prop="mean" :label="isEn ? 'Mean' : '平均值'" width="160" />
        <el-table-column prop="median" :label="isEn ? 'Median' : '中位数'" width="160" />
        <el-table-column prop="count" :label="isEn ? 'Sample Count' : '样本数'" width="120" />
      </el-table>
    </div>
  </div>
</template>

<script>
import { listPatients, getStatisticsSummary, exportStatisticsCsv } from "@/api";

const METRICS = [
  ["Psoas HU(mean)", "psoas_hu_mean"],
  ["Psoas Area(mm²)", "psoas_area_mm2"],
  ["Psoas Volume(mm³)", "psoas_volume_mm3"],
  ["Combo HU(mean)", "combo_hu_mean"],
  ["Combo Area(mm²)", "combo_area_mm2"],
  ["Total Muscle Volume(mm³)", "full_muscle_volume_mm3"],
  ["SAT HU", "sat_hu_mean"],
  ["SAT Area(mm²)", "sat_area_mm2"],
  ["VAT HU", "vat_hu_mean"],
  ["VAT Area(mm²)", "vat_area_mm2"],
];

const METRICS_ZH = [
  ["Psoas HU(mean)", "psoas_hu_mean"],
  ["Psoas 面积(mm²)", "psoas_area_mm2"],
  ["腰大肌体积(mm³)", "psoas_volume_mm3"],
  ["Combo HU(mean)", "combo_hu_mean"],
  ["Combo 面积(mm²)", "combo_area_mm2"],
  ["全肌肉体积(mm³)", "full_muscle_volume_mm3"],
  ["SAT HU", "sat_hu_mean"],
  ["SAT 面积(mm²)", "sat_area_mm2"],
  ["VAT HU", "vat_hu_mean"],
  ["VAT 面积(mm²)", "vat_area_mm2"],
];

export default {
  name: "ResultStatistics",
  data() {
    return {
      loading: false,
      statsLoading: false,
      downloading: false,
      rows: [],
      selectedKeys: [],
      statsScope: "",
      statsSummary: null,
      loadedOwnerName: "",
    };
  },
  created() {
    this.fetchPatients();
  },
  computed: {
    isEn() {
      return this.$i18n.locale === "en";
    },
    metricDefs() {
      return this.isEn ? METRICS : METRICS_ZH;
    },
    ownerId() {
      return this.$route.query.owner_id || null;
    },
    ownerName() {
      return this.$route.query.owner_name || this.loadedOwnerName || this.ownerId;
    },
    isAllScope() {
      return this.statsScope === "__all__";
    },
    selectedStatsRow() {
      return this.rows.find((row) => row.display === this.statsScope) || null;
    },
    statsRows() {
      return this.buildStatsRows(this.statsSummary);
    },
    downloadDisabled() {
      return this.isAllScope ? this.selectedRows.length === 0 : this.statsRows.length === 0;
    },
    selectedRows() {
      return this.selectedKeys
        .map((key) => this.rows.find((row) => row.display === key))
        .filter(Boolean);
    },
    allPatientsSelected() {
      return this.rows.length > 0 && this.selectedRows.length === this.rows.length;
    },
  },
  methods: {
    async fetchPatients() {
      this.loading = true;
      try {
        const res = await listPatients(this.ownerId);
        const list = res && res.data && res.data.patients ? res.data.patients : [];
        this.loadedOwnerName = (res && res.data && res.data.owner_username) || "";
        this.rows = list.map((item) => {
          const i = item.lastIndexOf("-");
          return {
            display: item,
            patient: item.slice(0, i),
            date: item.slice(i + 1),
          };
        });
        if (this.$route.query.scope === "all") {
          this.statsScope = "__all__";
        } else if (!this.statsScope) {
          this.statsScope = this.rows.length ? this.rows[0].display : "__all__";
        }
        if (!this.selectedKeys.length) {
          this.selectedKeys = this.rows.map((row) => row.display);
        }
        await this.refreshCurrent();
      } catch (e) {
        this.$message.error(this.$t("messages.fetchFail"));
      } finally {
        this.loading = false;
      }
    },
    async onScopeChange() {
      await this.refreshCurrent();
      if (this.isAllScope) this.selectAllPatients();
    },
    async refreshCurrent() {
      if (this.isAllScope) {
        this.statsSummary = null;
        return;
      }
      await this.fetchStats();
    },
    selectAllPatients() {
      this.selectedKeys = this.rows.map((row) => row.display);
    },
    toggleSelectAllPatients() {
      this.selectedKeys = this.allPatientsSelected ? [] : this.rows.map((row) => row.display);
    },
    togglePatient(row, checked) {
      if (checked) {
        if (!this.selectedKeys.includes(row.display)) this.selectedKeys = [...this.selectedKeys, row.display];
      } else {
        this.selectedKeys = this.selectedKeys.filter((key) => key !== row.display);
      }
    },
    openBatchMiddleStats() {
      if (!this.selectedRows.length) {
        this.$message.warning(this.isEn ? "Select patients first" : "请先选择要统计的患者");
        return;
      }
      const batchId = `batch_stats_${Date.now()}`;
      const patients = this.selectedRows.map((row) => ({
        patient_name: row.patient,
        study_date: row.date,
        display: row.display,
      }));
      sessionStorage.setItem(batchId, JSON.stringify({
        patients,
        ownerId: this.ownerId,
        ownerName: this.ownerName,
      }));
      const query = { batch_id: batchId };
      if (this.ownerId) query.owner_id = this.ownerId;
      if (this.ownerName) query.owner_name = this.ownerName;
      this.$router.push({ path: "/results/batch-statistics", query });
    },
    async fetchStats() {
      const selected = this.selectedStatsRow;
      if (!selected) {
        this.statsSummary = null;
        return;
      }
      this.statsLoading = true;
      try {
        const res = await getStatisticsSummary(this.ownerId, {
          patient_name: selected.patient,
          study_date: selected.date,
        });
        this.statsSummary = (res && res.data) || null;
      } catch (e) {
        this.statsSummary = null;
        this.$message.error(e.response?.data?.detail || (this.isEn ? "Failed to fetch statistics" : "统计数据获取失败"));
      } finally {
        this.statsLoading = false;
      }
    },
    async downloadStatsCsv() {
      if (this.isAllScope) {
        await this.downloadSelectedPatientFiles();
        return;
      }
      const selected = this.selectedStatsRow;
      if (!selected) return;
      await this.downloadPatientZip([selected]);
    },
    async downloadSelectedPatientFiles() {
      if (!this.selectedRows.length) {
        this.$message.warning(this.isEn ? "Select patients first" : "请先选择要下载的患者");
        return;
      }
      await this.downloadPatientZip(this.selectedRows);
    },
    async downloadPatientZip(rows) {
      this.downloading = true;
      try {
        const patients = rows.map((row) => ({
          patient_name: row.patient,
          study_date: row.date,
        }));
        const res = await exportStatisticsCsv(this.ownerId, patients, this.ownerName || null);
        const disposition = res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"]);
        const filename = this.downloadFilenameFromDisposition(disposition) || `${this.accountPrefix()}_statistics_csv.zip`;
        this.downloadBlob(res.data, filename);
        this.$message.success(
          this.isEn
            ? `Downloaded ${patients.length} result package(s)`
            : `已下载 ${patients.length} 个结果压缩包`
        );
      } catch (e) {
        this.$message.error(e.response?.data?.detail || (this.isEn ? "CSV export failed" : "统计 CSV 下载失败"));
      } finally {
        this.downloading = false;
      }
    },
    downloadFilenameFromDisposition(disposition) {
      if (!disposition) return "";
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match) return decodeURIComponent(utf8Match[1]);
      const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);
      return asciiMatch ? asciiMatch[1] : "";
    },
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadOneCsv(tableRows, filename) {
      const header = this.isEn ? ["Metric", "Mean", "Median", "Sample Count"] : ["指标", "平均值", "中位数", "样本数"];
      const escapeCell = (value) => `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
      const lines = [
        header.map(escapeCell).join(","),
        ...tableRows.map((row) =>
          [row.label, row.mean, row.median, row.count].map(escapeCell).join(",")
        ),
      ];
      const blob = new Blob(["\uFEFF", lines.join("\r\n")], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    statsFilePrefix() {
      const selected = this.selectedStatsRow;
      return selected ? `${this.accountPrefix()}_${selected.patient}_${selected.date}` : `${this.accountPrefix()}_statistics`;
    },
    accountPrefix() {
      return this.ownerId ? this.ownerName : (this.isEn ? "CurrentAccount" : "当前账号");
    },
    buildStatsRows(summary) {
      if (!summary) return [];
      const metrics = summary.metrics || {};
      return this.metricDefs.map(([label, key]) => {
        const item = metrics[key] || {};
        return {
          label,
          mean: this.fmt(item.mean),
          median: this.fmt(item.median),
          count: Array.isArray(item.values) ? item.values.length : summary.count || 0,
        };
      });
    },
    fmt(value) {
      return value == null || Number.isNaN(Number(value)) ? "-" : Number(value).toFixed(2);
    },
    goBack() {
      const query = {};
      if (this.ownerId) query.owner_id = this.ownerId;
      if (this.ownerName) query.owner_name = this.ownerName;
      this.$router.push({ path: "/results", query });
    },
  },
};
</script>

<style scoped>
.stats-page {
  width: clamp(960px, 86vw, 1140px);
  margin: 24px auto 80px;
}

.card {
  background: #ffffff;
  border-radius: 14px;
  padding: 20px 16px 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}

.head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.title {
  margin: 0 4px;
  color: #0f172a;
  font-weight: 800;
}

.desc {
  margin: 6px 4px 0;
  color: #64748b;
  font-size: 13px;
}

.spacer {
  flex: 1;
}

.stats-select {
  width: 260px;
}

:deep(.el-table th) {
  background: #f8fafc;
  color: #0f172a;
  font-weight: 700;
}

:deep(.el-table .cell) {
  color: #111827;
}
</style>
